/**
 * Ferestrele ecranului Rambursuri curieri, mutate din vechea pagina (12.10.2026) fara schimbari de
 * comportament: potrivirea manuala, setarile curierilor, importul fisierului Sameday.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { type Ramburs, type Candidat, lei, mesajServer } from './tipuri'
import { SetariUrgentare } from './urgentare'

export function PotrivireManuala({ ramburs, onClose }: { ramburs: Ramburs; onClose: () => void }) {
  const qc = useQueryClient()
  const [docType, setDocType] = useState('fac')
  const [docNo, setDocNo] = useState('')

  /**
   * Facturi cu exact aceeași sumă, emise înainte de virament. Pentru AWB-urile generate manual
   * (fără număr de comandă în text) asta e singurul fir — și de obicei duce direct la factură.
   */
  const { data: cand } = useQuery<{ candidati: Candidat[]; unic: boolean }>({
    queryKey: ['fan', 'candidati', ramburs.id],
    queryFn: async () => (await api.get(`/fan/ramburs/${ramburs.id}/candidati`)).data,
  })

  const m = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/fan/ramburs/${ramburs.id}/potriveste`, {
          eaDocType: docType,
          eaDocNo: docNo,
        })
      ).data,
    onSuccess: () => {
      toast.success('Rambursul a fost legat de factură.')
      void qc.invalidateQueries({ queryKey: ['fan'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Nu s-a putut lega de factură.')),
  })

  return (
    <Modal open onClose={onClose} title={`Potrivire manuală — AWB ${ramburs.awbNo}`}>
      <div className="space-y-3 text-sm">
        <p className="text-b4l-text-muted">
          {ramburs.matchErrorLabel}. Colectat: <strong>{lei(ramburs.amountCollected)} lei</strong>,
          destinatar {ramburs.recipientName ?? '—'}.
        </p>
        {ramburs.content && (
          <p className="rounded bg-b4l-bg px-2 py-1.5 text-xs">
            Text AWB: <span className="font-mono">{ramburs.content}</span>
          </p>
        )}

        {cand && cand.candidati.length > 0 && (
          <div className="rounded border border-b4l-border">
            <div className="border-b border-b4l-border px-2 py-1.5 text-xs text-b4l-text-muted">
              {cand.unic
                ? 'O singură factură are exact această sumă în perioada dinaintea viramentului:'
                : `${cand.candidati.length} facturi au exact această sumă — alege-o pe cea potrivită:`}
            </div>
            <ul>
              {cand.candidati.map((c) => (
                <li
                  key={`${c.docType}-${c.docNo}`}
                  className="flex items-center justify-between gap-2 border-b border-b4l-border/60 px-2 py-1.5 last:border-0"
                >
                  <span>
                    <span className="font-medium">{c.docNo}</span>{' '}
                    <span className="text-xs text-b4l-text-muted">
                      {c.partnerName ?? '—'} · {c.docns ?? 'fără comandă'} · cu {c.zileInainte}{' '}
                      {c.zileInainte === 1 ? 'zi' : 'zile'} înainte
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDocType(c.docType)
                      setDocNo(c.docNo)
                    }}
                  >
                    Alege
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex gap-3">
          <label className="block w-32">
            <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Tip document</span>
            <select
              className="w-full rounded border border-b4l-border bg-white px-2 py-2 text-sm"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              <option value="fac">fac</option>
              <option value="fac_boost">fac_boost</option>
            </select>
          </label>
          <label className="block flex-1">
            <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Număr factură</span>
            <input
              className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
              placeholder="ex. SRT104381"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value.trim())}
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!docNo}>
            Leagă de factură
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/** Credențialele SelfAWB. Parola nu se citește niciodată înapoi — doar se înlocuiește. */
interface Contabile {
  glAccount: string
  payDoct: string
  feeTolerance: string
}

/**
 * Setările contabile ale unui curier, aceleași trei pentru toți.
 *
 * Stăteau în setările FAN, într-un singur exemplar pentru toată lumea — și câmpul contului era
 * randat de două ori, identic, dintr-un copy/paste. Acum sunt un rând per curier, în tabela lor.
 */
function useContabile(courier: 'FAN' | 'SAMEDAY') {
  const { data } = useQuery<{
    curieri: { courier: string; glAccount: string; payDoct: string; feeTolerance: number }[]
  }>({
    queryKey: ['fan', 'curieri-settings'],
    queryFn: async () => (await api.get('/fan/curieri-settings')).data,
  })

  const [v, setV] = useState<Contabile>({ glAccount: '', payDoct: '', feeTolerance: '20' })
  const [initializat, setInitializat] = useState(false)

  const rand = data?.curieri.find((c) => c.courier === courier)
  if (rand && !initializat) {
    setV({
      glAccount: rand.glAccount,
      payDoct: rand.payDoct,
      feeTolerance: String(rand.feeTolerance),
    })
    setInitializat(true)
  }

  const salveaza = async (): Promise<void> => {
    await api.put(`/fan/curieri-settings/${courier}`, {
      glAccount: v.glAccount,
      payDoct: v.payDoct,
      feeTolerance: Number(v.feeTolerance) || 0,
    })
  }

  return { v, setV, salveaza }
}

function CampuriContabile({
  v,
  setV,
  editabil,
}: {
  v: Contabile
  setV: (v: Contabile) => void
  editabil: boolean
}) {
  return (
    <>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
          Cont pentru încasările colectate de curier
        </span>
        <input
          className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
          placeholder="5125.curier"
          value={v.glAccount}
          disabled={!editabil}
          onChange={(e) => setV({ ...v, glAccount: e.target.value.trim() })}
        />
        <span className="mt-0.5 block text-xs text-b4l-text-muted">
          Analitic separat per curier, ca soldul să arate oricând câți bani ține fiecare.
        </span>
      </label>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
          Tip document de plată în ExpertAccounts
        </span>
        <input
          className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
          placeholder="card"
          value={v.payDoct}
          disabled={!editabil}
          onChange={(e) => setV({ ...v, payDoct: e.target.value })}
        />
        <span className="mt-0.5 block text-xs text-b4l-text-muted">
          Cod de tip de document din EA, nu denumire — „card” produce în jurnal explicația „card
          payment”.
        </span>
      </label>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
          Taxă acceptată peste valoarea facturii (lei)
        </span>
        <input
          className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
          value={v.feeTolerance}
          disabled={!editabil}
          onChange={(e) => setV({ ...v, feeTolerance: e.target.value })}
        />
        <span className="mt-0.5 block text-xs text-b4l-text-muted">
          Clientul plătește uneori o taxă care nu ajunge pe factură. Sub acest prag, rambursul se
          potrivește oricum — dar în EA se trimite valoarea facturii, nu suma colectată. Peste prag,
          rămâne excepție. Sub valoarea facturii nu se acceptă niciodată.
        </span>
      </label>
    </>
  )
}

function SetariFan({ editabil, onClose }: { editabil: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data } = useQuery<{
    baseUrl: string
    username: string
    clientId: string
    enabled: boolean
    glAccount: string
    feeTolerance: number
    payDoct: string
    areParola: boolean
  }>({
    queryKey: ['fan', 'settings'],
    queryFn: async () => (await api.get('/fan/settings')).data,
  })

  const [username, setUsername] = useState('')
  const [clientId, setClientId] = useState('')
  const [password, setPassword] = useState('')
  const [enabled, setEnabled] = useState(false)
  const contabile = useContabile('FAN')
  const [initializat, setInitializat] = useState(false)

  if (data && !initializat) {
    setUsername(data.username)
    setClientId(data.clientId)
    setEnabled(data.enabled)
    setInitializat(true)
  }

  const salveaza = useMutation({
    mutationFn: async () => {
      await api.put('/fan/settings', {
        username,
        clientId,
        enabled,
        ...(password ? { password } : {}),
      })
      // Credentialele si partea contabila stau in tabele diferite, dar pentru om e un singur
      // „Salvează".
      await contabile.salveaza()
    },
    onSuccess: () => {
      toast.success('Setările FAN au fost salvate.')
      void qc.invalidateQueries({ queryKey: ['fan'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Salvarea a eșuat.')),
  })

  // Testul se face pe ce e SALVAT, nu pe ce e in formular — altfel ar spune „merge" pentru
  // niste date care n-au ajuns inca in baza.
  const testeaza = useMutation({
    mutationFn: async () => (await api.post('/fan/test', {})).data,
    onSuccess: (r: { ok: boolean; message: string }) =>
      r.ok ? toast.success(r.message) : toast.error(r.message),
    onError: () => toast.error('Testul n-a putut fi rulat.'),
  })

  return (
    <>
      <div className="space-y-3 text-sm">
        <p className="text-b4l-text-muted">
          Datele de acces din SelfAWB. <code>clientId</code> e codul de client FAN, obligatoriu pe
          fiecare apel de raport.
        </p>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Utilizator</span>
          <input
            className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
            value={username}
            disabled={!editabil}
            onChange={(e) => setUsername(e.target.value.trim())}
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
            Parolă {data?.areParola && '(lasă gol ca să o păstrezi pe cea salvată)'}
          </span>
          <input
            type="password"
            className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
            value={password}
            disabled={!editabil}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">clientId</span>
          <input
            className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
            value={clientId}
            disabled={!editabil}
            onChange={(e) => setClientId(e.target.value.trim())}
          />
        </label>
        <CampuriContabile v={contabile.v} setV={contabile.setV} editabil={editabil} />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!editabil}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>Activează sincronizarea zilnică</span>
        </label>
        <div className="flex items-center justify-end gap-2 pt-1">
          {editabil && (
            <Button
              variant="ghost"
              className="mr-auto"
              onClick={() => testeaza.mutate()}
              loading={testeaza.isPending}
            >
              Testează conexiunea
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {editabil ? 'Renunță' : 'Închide'}
          </Button>
          {editabil && (
            <Button onClick={() => salveaza.mutate()} loading={salveaza.isPending}>
              Salvează
            </Button>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Setările Sameday: DOAR accesul la API.
 *
 * Nu are `clientId` (Sameday nu cere), nici cont contabil sau prag de taxă — acelea vin cu
 * importul de rambursuri, într-un loc comun tuturor curierilor, ca să nu existe două copii ale
 * acelorași trei câmpuri.
 */
function SetariSameday({ editabil, onClose }: { editabil: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data } = useQuery<{
    baseUrl: string
    username: string
    enabled: boolean
    areParola: boolean
    lastSyncAt: string | null
    lastError: string | null
  }>({
    queryKey: ['fan', 'sameday-settings'],
    queryFn: async () => (await api.get('/fan/sameday-settings')).data,
  })

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [initializat, setInitializat] = useState(false)
  const contabile = useContabile('SAMEDAY')

  if (data && !initializat) {
    setUsername(data.username)
    setEnabled(data.enabled)
    setInitializat(true)
  }

  const salveaza = useMutation({
    mutationFn: async () => {
      await api.put('/fan/sameday-settings', {
        username,
        enabled,
        ...(password ? { password } : {}),
      })
      await contabile.salveaza()
    },
    onSuccess: () => {
      toast.success('Setările Sameday au fost salvate.')
      void qc.invalidateQueries({ queryKey: ['fan'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(mesajServer(e, 'Salvarea a eșuat.')),
  })

  // Ca la FAN: testul merge pe ce e SALVAT, nu pe ce e în formular.
  const testeaza = useMutation({
    mutationFn: async () => (await api.post('/fan/sameday-test', {})).data,
    onSuccess: (r: { ok: boolean; message: string }) =>
      r.ok ? toast.success(r.message) : toast.error(r.message),
    onError: () => toast.error('Testul n-a putut fi rulat.'),
  })

  return (
    <div className="space-y-3 text-sm">
      <p className="text-b4l-text-muted">
        Utilizatorul și parola de API Sameday (nu cele din eAWB, dacă diferă). Se folosesc doar
        pentru urmărirea livrărilor: API-ul Sameday nu are niciun raport de ramburs, așa că
        încasările vin din fișierul primit pe email.
      </p>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Utilizator</span>
        <input
          className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
          value={username}
          disabled={!editabil}
          onChange={(e) => setUsername(e.target.value.trim())}
        />
      </label>
      <label className="block">
        <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
          Parolă {data?.areParola && '(lasă gol ca să o păstrezi pe cea salvată)'}
        </span>
        <input
          type="password"
          className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
          value={password}
          disabled={!editabil}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span className="mt-0.5 block text-xs text-b4l-text-muted">
          Salvarea unei parole noi șterge și tokenul păstrat — cel vechi a fost emis pe alte
          credențiale.
        </span>
      </label>
      <CampuriContabile v={contabile.v} setV={contabile.setV} editabil={editabil} />
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!editabil}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Activează</span>
      </label>
      {data?.lastError && (
        <p className="rounded border border-b4l-red/30 bg-b4l-red-light/30 p-2 text-xs">
          Ultima eroare: {data.lastError}
        </p>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        {editabil && (
          <Button
            variant="ghost"
            className="mr-auto"
            onClick={() => testeaza.mutate()}
            loading={testeaza.isPending}
          >
            Testează conexiunea
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>
          {editabil ? 'Renunță' : 'Închide'}
        </Button>
        {editabil && (
          <Button onClick={() => salveaza.mutate()} loading={salveaza.isPending}>
            Salvează
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Importul fișierului „ramburs de transferat" de la Sameday.
 *
 * API-ul Sameday nu are niciun raport de viramente, deci asta e singura cale prin care aflăm ce
 * a colectat curierul și pentru care comandă. Fișierul vine pe email; aici se urcă.
 *
 * Raportul de după import arată suma de control, nu doar câte rânduri s-au salvat: un fișier
 * trunchiat se vede doar din sumă, iar diferența dintre ce scrie în antet și ce s-a importat e
 * exact banii care n-au intrat nicăieri.
 */
export function ImportSameday({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)

  interface Sarit {
    rand: number
    motiv: string
    suma: number
  }
  interface Rezultat {
    fisier: string | null
    transferDate: string
    citite: number
    adaugate: number
    actualizate: number
    potrivite: number
    exceptii: number
    sarite_trimise: number
    sarite: Sarit[]
    suma_fisier: number
    suma_importata: number
  }
  const [rez, setRez] = useState<Rezultat | null>(null)

  const importa = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData()
      fd.append('file', f)
      return (
        await api.post<Rezultat>('/fan/sameday-import', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120_000,
        })
      ).data
    },
    onSuccess: (r) => {
      setRez(r)
      void qc.invalidateQueries({ queryKey: ['fan'] })
      toast.success(`${r.adaugate} adăugate, ${r.actualizate} actualizate.`)
    },
    onError: (e: { response?: { data?: { message?: string; error?: { message?: string } } } }) =>
      toast.error(
        e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Importul a eșuat.',
      ),
  })

  const lei = (n: number) => n.toLocaleString('ro-RO', { minimumFractionDigits: 2 })

  return (
    <Modal open onClose={onClose} title="Import ramburs Sameday">
      <div className="space-y-3 text-sm">
        {!rez && (
          <>
            <p className="text-b4l-text-muted">
              Fișierul <code>…-ramburs-de-transferat-ron.xlsx</code>, exact cum vine pe email. Ziua
              viramentului se ia din numele fișierului, așa că nu-l redenumi.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="w-full rounded border border-b4l-border px-2 py-2 text-sm"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={onClose}>
                Renunță
              </Button>
              <Button
                disabled={!file}
                loading={importa.isPending}
                onClick={() => file && importa.mutate(file)}
              >
                Importă
              </Button>
            </div>
          </>
        )}

        {rez && (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-b4l-text-muted">Zi virament</dt>
              <dd className="tabular-nums">{rez.transferDate.split('-').reverse().join('.')}</dd>
              <dt className="text-b4l-text-muted">Rânduri</dt>
              <dd>
                {rez.adaugate} adăugate · {rez.actualizate} actualizate · {rez.potrivite} potrivite
                · {rez.exceptii} excepții
              </dd>
              <dt className="text-b4l-text-muted">Sumă fișier</dt>
              <dd className="tabular-nums">{lei(rez.suma_fisier)} lei</dd>
              <dt className="text-b4l-text-muted">Sumă importată</dt>
              <dd className="tabular-nums">
                {lei(rez.suma_importata)} lei
                {Math.abs(rez.suma_fisier - rez.suma_importata) > 0.01 && (
                  <span className="ml-2 text-b4l-red-dark">
                    lipsesc {lei(rez.suma_fisier - rez.suma_importata)} lei
                  </span>
                )}
              </dd>
            </dl>

            {rez.sarite.length > 0 && (
              <div>
                <p className="mb-1 font-medium">Rânduri sărite ({rez.sarite.length})</p>
                <ul className="max-h-48 space-y-0.5 overflow-y-auto text-xs text-b4l-text-muted">
                  {rez.sarite.map((s) => (
                    <li key={`${s.rand}-${s.motiv}`}>
                      rândul {s.rand} · {lei(s.suma)} lei — {s.motiv}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button onClick={onClose}>Închide</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * Un singur modal pentru amândoi curierii.
 *
 * Ecranul e comun de la început („Rambursuri curieri"), deci și setările lui. Un al doilea buton
 * „Setări Sameday" ar fi mers acum, la doi curieri, dar nu și la al treilea.
 */
export function SetariCurieri({ editabil, onClose }: { editabil: boolean; onClose: () => void }) {
  const [fila, setFila] = useState<'FAN' | 'SAMEDAY' | 'URGENTARE'>('FAN')
  const ETICHETE = { FAN: 'FAN Courier', SAMEDAY: 'Sameday', URGENTARE: 'Urgentare' } as const

  return (
    <Modal open onClose={onClose} title="Setări curieri" size={fila === 'URGENTARE' ? 'lg' : 'md'}>
      <div className="mb-3 flex gap-1 border-b border-b4l-border">
        {(['FAN', 'SAMEDAY', 'URGENTARE'] as const).map((c) => (
          <button
            key={c}
            onClick={() => setFila(c)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              fila === c
                ? 'border-b4l-blue font-medium text-b4l-blue'
                : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
            }`}
          >
            {ETICHETE[c]}
          </button>
        ))}
      </div>
      {fila === 'FAN' ? (
        <SetariFan editabil={editabil} onClose={onClose} />
      ) : fila === 'SAMEDAY' ? (
        <SetariSameday editabil={editabil} onClose={onClose} />
      ) : (
        <SetariUrgentare editabil={editabil} />
      )}
    </Modal>
  )
}

/**
 * Palierul 3: banii colectați de curier au ajuns în cont?
 *
 * O zi e „confirmată" doar când s-a găsit în extras o combinație de încasări care dă exact
 * suma colectată. Nu aproximăm și nu alocăm parțial — o zi pe jumătate confirmată n-ar
 * însemna nimic, iar diferența e tocmai cifra care trebuie urmărită.
 */
